/* eslint-disable */
// Airtag landing behaviour — lifted from new-ui/export/index.html (the design
// tool's `AirtagLanding` class) with four deliberate changes:
//
//   1. every addEventListener / IntersectionObserver goes through _on/_io so
//      componentWillUnmount can release them. The export leaked all of them,
//      which is harmless on a static page and not harmless inside a router.
//   2. the referral code comes from `opts.referralCode`, not a constant
//   3. setLang calls `opts.onLang`, so real i18n owns the language
//   4. the self-bootstrapping IIFE is dropped; LandingV2 mounts it
//
// Everything else is byte-identical, so a re-export diffs cleanly.

// Module-level in the export's <script>, above the class. Referenced by ten
// call sites inside it, so it has to come across too.
const EASE = 'cubic-bezier(.22,.61,.36,1)';

/* The behaviour instance the chrome currently has mounted. React components use it
   via openTopUp() below; see that comment for why delegation is not enough. */
let activeInstance = null;

class AirtagLanding {
  /* Mount is the ONLY place that writes styles, and there is deliberately
     no per-update hook: writing a style or attribute during a re-render
     reads back as a mutation, which triggers another render — an unbounded
     loop. Interaction is delegated on document instead, so it survives
     re-renders without touching the DOM at all. */
  componentDidMount() {
    activeInstance = this;
    this.guardTimeline();

    /* Interaction first, and unconditionally.
     *
     * prefers-reduced-motion asks for less movement -- it is not a request for a
     * storefront that ignores taps. delegate() is the only thing binding
     * [data-topup-open], [data-topup-close], [data-burger], [data-navlink],
     * [data-lang-opt], [data-copy] and [data-tab], so running it after the motion
     * check left every one of those dead for anyone with Reduce Motion switched on
     * -- a standard accessibility setting on iOS and Android. The Top-Up button in
     * the cart's Insufficient Coins dialog was one of the casualties.
     *
     * paintLang / showTab / setMenu are the matching initial states for those same
     * controls (active language, the chooser's default tab, the menu's closed
     * state), so they belong on this side of the check too.
     */
    this.delegate();
    let lang = 'EN';
    try { lang = localStorage.getItem('airtag-lang') || 'EN'; } catch (e) {}
    this.paintLang(lang);
    this.showTab('code');
    this.setMenu(false);

    /* Everything below is decoration: reveals, counters, scroll effects and hover
       flourishes. This is what Reduce Motion is actually asking us to skip. */
    if (!this.motionOn()) return;

    this.boot();
    this.scrollFx();
    if (this._onScroll) {
      this._on(document, 'scroll', this._onScroll, { passive: true, capture: true });
      this._on(window, 'scroll', this._onScroll, { passive: true });
      this._on(window, 'resize', this._onScroll);
      this._on(window, 'wheel', this._onScroll, { passive: true });
      this._on(window, 'touchmove', this._onScroll, { passive: true });
      this._timer = setInterval(this._onScroll, 90);
    }
    this.pointerFx();
    this.stepFocus();
    if (this._apply) this._apply();
  }

  /* Added for the SPA port. The export only cleared its timers because a
     static page is torn down by navigation; inside the router this component
     mounts and unmounts repeatedly, so every listener and observer has to be
     released or they stack up on each visit. */
  _on(target, type, fn, opts) {
    (this._listeners || (this._listeners = [])).push([target, type, fn, opts]);
    target.addEventListener(type, fn, opts);
  }

  _io(cb, opts) {
    const o = new IntersectionObserver(cb, opts);
    (this._observers || (this._observers = [])).push(o);
    return o;
  }

  componentWillUnmount() {
    if (this._safety) clearTimeout(this._safety);
    if (this._timer) clearInterval(this._timer);
    if (this._raf) cancelAnimationFrame(this._raf);
    (this._observers || []).forEach((o) => o.disconnect());
    (this._listeners || []).forEach(([t, type, fn, opts]) => t.removeEventListener(type, fn, opts));
    this._observers = [];
    this._listeners = [];
    this._delegated = false;
    this._pointerBound = false;
    if (activeInstance === this) activeInstance = null;
  }

  motionOn() {
    if (window.AIRTAG_MOTION === false) return false;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return true;
  }

  boot() {
    this.reveal();
    this.headlines();
    this.counters();
    this.zooms();
    this.hovers();
    /* Safety net: if an observer never fires (clone/print contexts,
       zero-height viewports), clear the pre-animation states. */
    /* Reads computed values via settleComputed — the inline values are
       already final here, so an inline-string check would skip exactly the
       elements that are still visually stuck. */
    this._safety = setTimeout(() => this.settleComputed(), 2600);
  }

  /* Channels stay separate so overlapping effects compose instead of
     clobbering: reveal owns `translate`, zoom owns `scale`, hover owns
     `transform` (applied after both by the CSS spec). */
  reveal() {
    const els = Array.from(document.querySelectorAll('[data-reveal]'));
    if (!els.length || !window.IntersectionObserver) return;
    els.forEach(el => {
      el.style.opacity = '0';
      el.style.translate = '0 26px';
      el.style.transition = 'opacity .72s ' + EASE + ', translate .72s ' + EASE;
      el.style.willChange = 'opacity, translate';
    });
    const io = this._io(entries => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const el = e.target;
        const sibs = Array.from(el.parentElement.querySelectorAll(':scope > [data-reveal]'));
        const i = Math.max(0, sibs.indexOf(el));
        setTimeout(() => {
          el.style.opacity = '1';
          el.style.translate = '0 0';
          setTimeout(() => { el.style.willChange = 'auto'; }, 800);
        }, i * 90);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach(el => io.observe(el));
  }

  headlines() {
    const els = Array.from(document.querySelectorAll('[data-headline]'));
    if (!els.length || !window.IntersectionObserver) return;
    els.forEach(el => {
      el.style.clipPath = 'inset(0 0 106% 0)';
      el.style.translate = '0 14px';
      el.style.transition = 'clip-path 1.05s ' + EASE + ', translate 1.05s ' + EASE;
    });
    const io = this._io(entries => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        e.target.style.clipPath = 'inset(0 0 -12% 0)';
        e.target.style.translate = '0 0';
        io.unobserve(e.target);
      });
    }, { threshold: 0.25 });
    els.forEach(el => io.observe(el));
  }

  zooms() {
    const els = Array.from(document.querySelectorAll('[data-zoom]'));
    if (!els.length || !window.IntersectionObserver) return;
    els.forEach(el => {
      el.style.scale = '1.1';
      el.style.transition = 'scale 1.3s ' + EASE + ', transform .5s ' + EASE;
    });
    const io = this._io(entries => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        e.target.style.scale = '1';
        io.unobserve(e.target);
      });
    }, { threshold: 0.2 });
    els.forEach(el => io.observe(el));
  }

  hovers() {
    this._tilted = this._tilted || new WeakSet();
    Array.from(document.querySelectorAll('[data-tilt]')).forEach(card => {
      if (this._tilted.has(card)) return;
      this._tilted.add(card);
      card.style.transition = (card.style.transition ? card.style.transition + ', ' : '') + 'transform .5s ' + EASE;
      const img = card.querySelector('[data-zoom]');
      this._on(card, 'mouseenter', () => {
        card.style.transform = 'translateY(-8px)';
        if (img) img.style.transform = 'scale(1.06)';
      });
      this._on(card, 'mouseleave', () => {
        card.style.transform = 'none';
        if (img) img.style.transform = 'none';
      });
    });
  }

  counters() {
    const els = Array.from(document.querySelectorAll('[data-count]'));
    if (!els.length || !window.IntersectionObserver) return;
    const io = this._io(entries => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const el = e.target;
        const target = parseInt(el.getAttribute('data-count'), 10) || 0;
        const t0 = performance.now();
        const tick = now => {
          const p = Math.min(1, (now - t0) / 900);
          el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.unobserve(el);
      });
    }, { threshold: 0.5 });
    els.forEach(el => io.observe(el));
  }

  delegate() {
    if (this._delegated) return;
    this._delegated = true;
    const SEL = '[data-copy],[data-topup-open],[data-topup-close],[data-topup-scrim],[data-topup-card],[data-tab],[data-topup-btn],[data-lang-opt],[data-burger],[data-navlink]';

    this._on(document, 'click', e => {
      const t = e.target.closest ? e.target.closest(SEL) : null;
      if (!t) return;
      if (t.hasAttribute('data-copy')) { e.preventDefault(); return this.doCopy(t); }
      if (t.hasAttribute('data-topup-open')) { e.preventDefault(); return this.openTopup(); }
      if (t.hasAttribute('data-topup-close') || t.hasAttribute('data-topup-scrim')) return this.closeTopup();
      if (t.hasAttribute('data-topup-card')) return this.revealCard();
      if (t.hasAttribute('data-tab')) return this.showTab(t.getAttribute('data-tab'));
      if (t.hasAttribute('data-topup-btn')) return this.submitCard();
      if (t.hasAttribute('data-lang-opt')) return this.setLang(t.getAttribute('data-lang-opt'));
      if (t.hasAttribute('data-burger')) return this.setMenu(!this._menuOpen);
      if (t.hasAttribute('data-navlink')) return this.setMenu(false);
    });

    this._on(document, 'keydown', e => {
      if (e.key !== 'Escape') return;
      const m = document.querySelector('[data-topup-modal]');
      if (m && m.style.display === 'flex') this.closeTopup();
    });

    const field = e => (e.target.closest ? e.target.closest('[data-cardinput]') : null);
    this._on(document, 'input', e => {
      const i = field(e);
      if (!i) return;
      i.value = i.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
      i.style.borderColor = '#3C028D';
      const hint = document.querySelector('[data-hint]');
      if (hint) { hint.textContent = 'Enter the 11-character code printed on your card.'; hint.style.color = '#6B6485'; }
    });
    this._on(document, 'focusin', e => { const i = field(e); if (i) i.style.borderColor = '#3C028D'; });
    this._on(document, 'focusout', e => { const i = field(e); if (i) i.style.borderColor = '#DCCCFA'; });
  }

  doCopy(btn) {
    // No 'R17WDRQP' fallback — copy is only reachable from a pill that is
    // itself gated on being signed in, so an empty code means something is
    // wrong and copying the export's placeholder would hide it.
    const code = (this.opts && this.opts.referralCode) || '';
    if (!code) return;
    const link = 'https://airtagcode.com/join?ref=' + code;
    const label = btn.querySelector('[data-copy-label]');
    const done = () => {
      if (!label || this._copyBusy) return;
      this._copyBusy = true;
      const was = label.textContent;
      label.textContent = 'COPIED';
      setTimeout(() => { label.textContent = was; this._copyBusy = false; }, 1600);
    };
    const text = btn.getAttribute('data-copy') === 'link' ? link : code;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, done);
    } else {
      const t = document.createElement('textarea');
      t.value = text; t.style.position = 'fixed'; t.style.opacity = '0';
      document.body.appendChild(t);
      t.select();
      try { document.execCommand('copy'); } catch (err) {}
      document.body.removeChild(t);
      done();
    }
  }

  setMenu(open, preRead) {
    const panel = document.querySelector('[data-mobilenav]');
    if (!panel) return;
    this._menuOpen = open;
    const inner = preRead !== undefined ? preRead : panel.firstElementChild;
    panel.style.maxHeight = open ? ((inner ? inner.scrollHeight : 340) + 'px') : '0';
    panel.style.opacity = open ? '1' : '0';
    this.commit(panel);
    const btn = document.querySelector('[data-burger]');
    if (!btn) return;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    const lines = btn.querySelectorAll('[data-burger-line]');
    if (lines.length === 3) {
      lines[0].style.transform = open ? 'translateY(6px) rotate(45deg)' : 'none';
      lines[1].style.opacity = open ? '0' : '1';
      lines[2].style.transform = open ? 'translateY(-6px) rotate(-45deg)' : 'none';
      lines.forEach(l => this.commit(l));
    }
  }

  showTab(which) {
    const host = document.querySelector('[data-topup]');
    if (!host) return;
    this._tab = which;
    Array.from(host.querySelectorAll('[data-tab]')).forEach(t => {
      const on = t.getAttribute('data-tab') === which;
      t.style.background = on ? '#3C028D' : 'transparent';
      t.style.color = on ? '#fff' : '#6B6485';
    });
    Array.from(host.querySelectorAll('[data-pane]')).forEach(p => {
      p.style.display = p.getAttribute('data-pane') === which ? 'flex' : 'none';
    });
    const label = host.querySelector('[data-topup-label]');
    if (label) label.textContent = which === 'qr' ? 'OPEN SCANNER' : 'TOP UP';
  }

  submitCard() {
    if (this._tab === 'qr') return;
    const host = document.querySelector('[data-topup]');
    if (!host) return;
    const input = host.querySelector('[data-cardinput]');
    const hint = host.querySelector('[data-hint]');
    const label = host.querySelector('[data-topup-label]');
    const v = input ? input.value.trim() : '';
    if (v.length !== 11) {
      if (hint) {
        hint.textContent = v ? 'That code is ' + v.length + ' of 11 characters.' : 'Enter your card code to continue.';
        hint.style.color = '#FF0555';
      }
      if (input) input.style.borderColor = '#FF0555';
      return;
    }
    if (hint) { hint.textContent = 'Card accepted — credit added to your wallet.'; hint.style.color = '#1D8A4E'; }
    if (label) label.textContent = 'TOPPED UP';
    setTimeout(() => { if (label) label.textContent = 'TOP UP'; }, 2200);
  }

  openTopup() {
    const modal = document.querySelector('[data-topup-modal]');
    if (!modal) return;
    if (this._menuOpen) this.setMenu(false);
    modal.style.display = 'flex';
    const scrim = modal.querySelector('[data-topup-scrim]');
    const sheet = modal.querySelector('[data-topup-sheet]');
    /* Force a reflow so the start state is registered, then write the end
       state synchronously — an rAF callback is not guaranteed to run. */
    void modal.offsetHeight;
    if (scrim) { scrim.style.opacity = '1'; this.commit(scrim); }
    if (sheet) { sheet.style.opacity = '1'; sheet.style.translate = '0 0'; this.commit(sheet); }
  }

  closeTopup() {
    const modal = document.querySelector('[data-topup-modal]');
    if (!modal) return;
    const scrim = modal.querySelector('[data-topup-scrim]');
    const sheet = modal.querySelector('[data-topup-sheet]');
    if (scrim) { scrim.style.opacity = '0'; this.commit(scrim); }
    if (sheet) { sheet.style.opacity = '0'; sheet.style.translate = '0 16px'; this.commit(sheet); }
    setTimeout(() => {
      modal.style.display = 'none';
      const form = modal.querySelector('[data-topup]');
      if (form) form.style.display = 'none';
    }, 300);
  }

  revealCard() {
    const form = document.querySelector('[data-topup]');
    if (!form) return;
    form.style.display = 'flex';
    this.showTab('code');
    const input = form.querySelector('[data-cardinput]');
    if (input) setTimeout(() => input.focus({ preventScroll: true }), 60);
  }

  setLang(lang) {
    if (this.opts && this.opts.onLang) this.opts.onLang(lang);
    try { localStorage.setItem('airtag-lang', lang); } catch (e) {}
    this.paintLang(lang);
  }

  paintLang(lang) {
    Array.from(document.querySelectorAll('[data-lang-opt]')).forEach(o => {
      const on = o.getAttribute('data-lang-opt') === lang;
      o.style.background = on ? '#3C028D' : 'transparent';
      o.style.color = on ? '#fff' : '#6B6485';
    });
    const want = lang === 'BM' ? 'ms' : 'en';
    if (document.documentElement.getAttribute('lang') !== want) {
      document.documentElement.setAttribute('lang', want);
    }
  }

  /* Cursor effects, also delegated. The spotlight loop carries a frame
     budget so it stops even if pointerleave never fires. */
  pointerFx() {
    if (this._pointerBound) return;
    if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) return;
    this._pointerBound = true;

    let tx = 0, ty = 0, cx = 0, cy = 0, on = false, raf = null, idle = 0;
    const glowOf = () => document.querySelector('[data-glow]');

    const loop = () => {
      cx += (tx - cx) * 0.09;
      cy += (ty - cy) * 0.09;
      const glow = glowOf();
      if (glow) glow.style.translate = cx.toFixed(1) + 'px ' + cy.toFixed(1) + 'px';
      const moving = Math.abs(tx - cx) > 0.5 || Math.abs(ty - cy) > 0.5;
      idle = moving ? 0 : idle + 1;
      raf = (moving || (on && idle < 60)) ? requestAnimationFrame(loop) : null;
    };
    const stop = () => {
      on = false;
      const glow = glowOf();
      if (glow) glow.style.opacity = '0';
      if (this._mag) {
        this._mag.style.transition = 'translate .45s ' + EASE;
        this._mag.style.translate = '0 0';
        this._mag = null;
      }
    };

    this._on(document, 'pointermove', e => {
      if (!e.target.closest) return;
      const host = e.target.closest('[data-spotlight]');
      if (host) {
        const r = host.getBoundingClientRect();
        tx = e.clientX - r.left;
        ty = e.clientY - r.top;
        if (!on) {
          on = true; cx = tx; cy = ty;
          const glow = glowOf();
          if (glow) { glow.style.transition = 'opacity .5s ease'; glow.style.opacity = '1'; }
        }
        idle = 0;
        if (!raf) raf = requestAnimationFrame(loop);
      } else if (on) {
        on = false;
        const glow = glowOf();
        if (glow) glow.style.opacity = '0';
      }

      const mag = e.target.closest('[data-magnetic]');
      if (this._mag && this._mag !== mag) {
        this._mag.style.transition = 'translate .45s ' + EASE;
        this._mag.style.translate = '0 0';
      }
      this._mag = mag || null;
      if (mag) {
        const r = mag.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
        const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
        mag.style.transition = 'translate .12s linear';
        mag.style.translate = (dx * 12).toFixed(1) + 'px ' + (dy * 8).toFixed(1) + 'px';
      }
    }, { passive: true });

    this._on(window, 'blur', stop);
    this._on(document, 'pointercancel', stop);
    this._on(window, 'pointerleave', stop);
  }

  /* Top-up modal: two routes in, card form revealed only on request. */

  stepFocus() {
    const steps = Array.from(document.querySelectorAll('[data-step]'));
    if (!steps.length) return;
    this._pickStep = vh => {
      let best = null, bestD = Infinity;
      const mid = vh / 2;
      steps.forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const d = Math.abs(r.top + r.height / 2 - mid);
        if (d < bestD) { bestD = d; best = el; }
      });
      return best;
    };
    this._paintStep = best => {
      steps.forEach(el => {
        const want = el === best ? '#F98412' : '#E3E1E9';
        if (el.style.borderTopColor !== want) el.style.borderTopColor = want;
      });
    };
  }

  scrollFx() {
    const header = document.querySelector('[data-header]');
    const inner = document.querySelector('[data-header-inner]');
    const bar = document.querySelector('[data-progress]');
    const par = Array.from(document.querySelectorAll('[data-parallax]'));
    const cue = document.querySelector('[data-scrollcue]');
    const arrow = document.querySelector('[data-cue-arrow]');
    const wbar = document.querySelector('[data-walletbar]');
    const coin = document.querySelector('[data-coinchip]');
    const burger = document.querySelector('[data-burger]');
    const dnav = document.querySelector('[data-desktopnav]');
    const band = document.querySelector('[data-shipband]');
    const route = document.querySelector('[data-r-route]');
    const truck = document.querySelector('[data-r-truck]');
    const routePins = Array.from(document.querySelectorAll('[data-r-pin]'));
    const routeLen = route && route.getTotalLength ? route.getTotalLength() : 0;
    if (route && routeLen) {
      route.style.strokeDasharray = routeLen;
      route.style.strokeDashoffset = routeLen;
    }
    let raf = null;

    const apply = () => {
      raf = null;
      /* READS first — no measurement after a write, so layout never has to
         flush mid-pass. */
      const y = window.pageYOffset;
      const vh = window.innerHeight;
      const narrow = window.innerWidth < 768;
      const max = Math.max(1, document.documentElement.scrollHeight - vh);
      const parMid = par.map(el => {
        const r = el.getBoundingClientRect();
        return r.top + r.height / 2 - vh / 2;
      });
      const dockInner = wbar ? wbar.firstElementChild : null;
      const dockH = dockInner ? dockInner.scrollHeight : 90;
      const menuPanel = document.querySelector('[data-mobilenav]');
      const menuInner = menuPanel ? menuPanel.firstElementChild : null;
      const stepBest = this._pickStep ? this._pickStep(vh) : null;
      /* Route progress: 0 as the band enters the lower third, 1 once it has
         travelled most of the way up — so the line draws while reading. */
      let routeP = null;
      if (band && routeLen) {
        const r = band.getBoundingClientRect();
        routeP = Math.max(0, Math.min(1, (vh * 0.86 - r.top) / (r.height + vh * 0.46)));
      }

      /* WRITES */
      if (bar) bar.style.width = Math.min(100, (y / max) * 100) + '%';
      if (header && inner) {
        const on = y > 12;
        inner.style.paddingTop = on ? '9px' : '';
        inner.style.paddingBottom = on ? '9px' : '';
        header.style.background = on ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.88)';
        header.style.boxShadow = on ? '0 6px 24px rgba(20,18,43,0.07)' : 'none';
      }
      par.forEach((el, i) => {
        const k = parseFloat(el.getAttribute('data-parallax')) || 0;
        el.style.transform = 'translate3d(0,' + (parMid[i] * k).toFixed(1) + 'px,0)';
      });
      if (cue) {
        const fade = Math.max(0, 1 - y / 220);
        cue.style.opacity = fade;
        cue.style.translate = '0 ' + ((1 - fade) * -8).toFixed(1) + 'px';
      }
      if (arrow) arrow.style.translate = '0 ' + (Math.sin(y / 60) * 2).toFixed(1) + 'px';
      /* Coin chip is redundant on phones — the wallet dock carries it. */
      // Chip visibility is CSS's job now: below 768px the balance moves to the
      // utility strip above the header, and an inline style set here would beat
      // the media query that hides the in-header chip.
      if (burger) burger.style.display = narrow ? 'flex' : 'none';
      if (dnav) dnav.style.display = narrow ? 'none' : 'flex';
      if (!narrow && this._menuOpen) this.setMenu(false, menuInner);
      /* Wallet docks under the header once the hero is behind you. Phones
         get the compact set: balance + referral only. */
      if (wbar) {
        /* [data-dock-off] lets a page opt out of the dock (the profile page
           does — a second sticky band on top of its form). Read per frame, not
           captured at mount, so it follows client-side route changes. */
        /* [data-dock-only] (Mystery Box / Redemption) shows its single balance from
           the top of the page, not only after scrolling. */
        const show = (y > 300 || wbar.hasAttribute('data-dock-only')) && !wbar.hasAttribute('data-dock-off');
        Array.from(wbar.querySelectorAll('[data-dock-full]')).forEach(el => {
          el.style.display = narrow ? 'none' : (el.tagName === 'A' ? 'inline-flex' : 'flex');
        });
        wbar.style.maxHeight = show ? (dockH + 'px') : '0';
        wbar.style.opacity = show ? '1' : '0';
        this.commit(wbar);
      }
      /* The corridor draws down the peninsula, the van rides the line, and
         each hub lights as it is passed. Driven by scroll position, so
         scrubbing back retracts it. */
      if (routeP !== null) {
        route.style.strokeDashoffset = (routeLen * (1 - routeP)).toFixed(1);
        if (truck) {
          const pt = route.getPointAtLength(routeLen * routeP);
          truck.setAttribute('transform', 'translate(' + pt.x.toFixed(1) + ',' + pt.y.toFixed(1) + ')');
          truck.style.opacity = routeP > 0.01 && routeP < 0.99 ? '1' : '0';
        }
        routePins.forEach(p => {
          const want = routeP >= parseFloat(p.getAttribute('data-at')) - 0.004 ? '1' : '0';
          if (p.style.opacity !== want) p.style.opacity = want;
        });
      }
      if (this._paintStep) this._paintStep(stepBest);
    };

    this._apply = apply;
    /* requestAnimationFrame is throttled to zero in some embedded containers,
       so an rAF-only scheduler can leave every scroll effect frozen at its
       mount value. Schedule a frame, and if it has not landed shortly after,
       run the pass directly. */
    this._onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(apply);
      setTimeout(() => { if (raf) { raf = null; apply(); } }, 40);
    };
    apply();
  }

  /* Some embedded containers run with a paused or frozen animation timeline:
     document.timeline.currentTime does not advance, so a CSS transition sits
     at frame 0 and any element whose readable state is the END of a
     transition never appears.

     The cure is to settle the affected elements, NOT to disable transitions:
     a timeline that is merely paused during load resumes later, and a global
     kill-switch would cost every hover, dock and modal transition for the
     rest of the session with no way back. So: sample for a while, and each
     time a stall is seen, finish outstanding animations and force the
     computed values to their end state. Motion still works the moment the
     timeline starts running. */
  guardTimeline() {
    const sample = () => {
      const t = document.timeline && document.timeline.currentTime;
      return typeof t === 'number' ? t : null;
    };
    /* A stale kill-switch from an earlier build must not survive. */
    const old = document.querySelector('style[data-om-no-tx]');
    if (old) old.remove();

    if (sample() === null) { this.settleNow(); return; }
    let prev = sample();
    [260, 700, 1500, 3000].forEach(ms => setTimeout(() => {
      const now = sample();
      if (now !== null && now - prev < 5) this.settleNow();
      prev = now;
    }, ms));
  }

  /* Writing an inline value is not enough when the container's animation
     timeline is frozen: the transition is created but pinned at frame 0, so
     the element never reaches the value. Finishing its animations right
     after the write makes the end state authoritative — and is a no-op when
     the timeline is healthy, since the transition has already been queued. */
  commit(el) {
    if (!el || !el.getAnimations) return;
    el.getAnimations().forEach(a => { try { a.finish(); } catch (e) {} });
  }

  settleNow() {
    if (document.getAnimations) {
      document.getAnimations().forEach(a => { try { a.finish(); } catch (e) {} });
    }
    this.settleComputed();
  }

  /* Reads COMPUTED values, not the inline strings: the inline value is
     already final by this point, so an inline check would skip every
     element that is still visually stuck. */
  settleComputed() {
    const cs = el => window.getComputedStyle(el);
    document.querySelectorAll('[data-reveal]').forEach(el => {
      if (parseFloat(cs(el).opacity) < 0.98) { el.style.opacity = '1'; el.style.translate = '0 0'; }
    });
    document.querySelectorAll('[data-headline]').forEach(el => {
      const cp = cs(el).clipPath;
      if (cp && cp !== 'none') { el.style.clipPath = 'none'; el.style.translate = '0 0'; }
    });
    document.querySelectorAll('[data-zoom]').forEach(el => {
      if (cs(el).scale && cs(el).scale !== 'none' && parseFloat(cs(el).scale) > 1.01) el.style.scale = '1';
    });
  }

}

/* Open the top-up chooser from React, without going through the document-level
 * [data-topup-open] delegation.
 *
 * That delegation only sees clicks that reach `document`. A control rendered inside
 * profile-chrome/Modal never gets there: the modal card stops propagation so that
 * clicking inside it does not trigger the overlay's close. The cart's "Insufficient
 * Coins" dialog sits in exactly that modal, so its Top-Up button closed the dialog
 * (its own onClick) and then nothing happened -- the chooser never opened.
 *
 * Delegation stays for the header controls, which are not inside a modal.
 */
export function openTopUp() {
    if (!activeInstance) return false;
    activeInstance.openTopup();
    return true;
}

export default AirtagLanding;
